// =============================================================================
// app/src/lib/fiadoDoc.ts — o EXTRATO DO FIADO: o que uma pessoa deve à loja.
//
// Pedido do dono (14/10/2026): "Preciso enviar para uma oficina o que eles
// devem, mas também preciso imprimir na loja para assinarmos em conjunto com o
// cliente quando ele está aqui". Na tela de Vendas, filtrando por Fiado,
// aparece "Emitir" — e daqui saem os dois papéis:
//
//   * IMPRIMIR NA LOJA: duas vias (cliente e loja), com as linhas para o
//     cliente e a loja assinarem juntos. É o reconhecimento da dívida.
//   * PDF: o mesmo extrato em arquivo, para mandar no WhatsApp da oficina —
//     ela confere venda por venda (dia, código do recibo, carro, peças).
//
// UM EXTRATO, DOIS DESENHOS. O conteúdo (quais vendas, quanto cada uma deve,
// a soma) é decidido UMA vez, em montarExtrato; o HTML e o PDF só desenham.
// Duas contas, uma em cada formato, um dia discordariam — e o papel assinado
// diria um total, o arquivo da oficina outro.
//
// NADA É GRAVADO. O saldo de cada venda vem de quitacao_da_venda (o banco);
// a soma é conta sobre ele, feita na hora (regra 4). Quem pode ver o quê é a
// RLS e a guarda daquela função: gerente e dono veem tudo; o vendedor, só as
// vendas dele.
//
// UMA FOLHA POR TRABALHO DA FILA. O agente da impressora fotografa um A4
// (794 × 1123 px) e corta o que passar — por isso o extrato é PAGINADO aqui,
// e cada página vira um trabalho. Conta de oficina tem dezenas de peças; sem
// paginação, a assinatura cairia fora da folha.
//
// As travas dos outros documentos valem aqui: todo campo do banco passa por
// escaparHtml e o documento declara CSP `script-src 'none'`.
// =============================================================================
import type { QuitacaoDaVenda } from '@core/types';
import { negocio } from '@negocio';
import { escaparHtml } from './aVerDoc';
import { brl as brlTela, dataBR, dataHoraBR, hojeISO, qtd } from './formato';
import { enderecoEmLinha } from './orcamentoDoc';
import { enderecoDaLoja, telefoneLegivel } from './orcamentoPdf';
import { CAMINHOS_MARCA, VIEWBOX_MARCA } from './marca';
import { interpretarCaminho } from './svgPath';
import { larguraDoTexto, novoPdf, quebrarEmLinhas, type Pdf } from './pdf';

// --- O conteúdo ---------------------------------------------------------------

export interface ItemDoExtrato {
  descricao: string;
  quantidade: number;
  subtotal: number;
  /** A peça já foi paga num acerto por peça (065). */
  paga: boolean;
}

export type SituacaoNoExtrato = 'aberta' | 'parcial' | 'quitada' | 'perdoada';

export interface VendaNoExtrato {
  saleId: string;
  codigo: string;
  vendida_em: string;
  /** O carro da venda, já legível — é o que a oficina usa para conferir. */
  veiculo: string | null;
  /** O que a venda deixou de dívida (contas_receber.valor). */
  valor: number;
  /** Dinheiro que já entrou nos acertos. */
  pago: number;
  /** Desconto negociado + peças devolvidas (065): abate sem dinheiro. */
  abatido: number;
  /** O que falta. Nunca negativo no papel. */
  saldo: number;
  situacao: SituacaoNoExtrato;
  itens: ItemDoExtrato[];
}

export interface ExtratoDoFiado {
  cliente: string;
  /** Quando o extrato foi montado (ISO) — "posição em". */
  emitidoEm: string;
  emitidoPor: string | null;
  vendas: VendaNoExtrato[];
  valor: number;
  pago: number;
  abatido: number;
  saldo: number;
}

/** O pedaço da venda da lista que o extrato usa. */
export interface VendaParaExtrato {
  id: string;
  codigo: string;
  vendida_em: string;
  total: number | string;
  total_cobrado?: number | string | null;
  veiculo: string | null;
}

/**
 * Uma venda fiada vira a linha do extrato.
 *
 * Sem `quitacao` (a venda não tem conta a receber — ou quem olha não pode
 * lê-la), a venda entra cobrando o total: é o que o recibo já faz, e o lado
 * certo para errar num papel que vai ser CONFERIDO pelo cliente.
 */
export function vendaNoExtrato(
  v: VendaParaExtrato,
  itens: Array<{ id?: string; descricao: string; quantidade: number | string; subtotal: number | string }>,
  quitacao: QuitacaoDaVenda | null,
): VendaNoExtrato {
  const pagas = new Set(quitacao?.itens_pagos ?? []);
  const total = Number(v.total_cobrado ?? v.total);
  const valor = quitacao ? Number(quitacao.valor) : total;
  const pago = quitacao ? Number(quitacao.recebido) : 0;
  const abatido = quitacao ? Number(quitacao.desconto ?? 0) + Number(quitacao.devolvido ?? 0) : 0;
  const saldoBruto = quitacao ? Number(quitacao.saldo) : total;
  const saldo = Math.max(0, Math.round(saldoBruto * 100) / 100);

  let situacao: SituacaoNoExtrato;
  if (quitacao?.perdido_em) situacao = 'perdoada';
  else if (saldo <= 0.005) situacao = 'quitada';
  else if (pago + abatido > 0.005) situacao = 'parcial';
  else situacao = 'aberta';

  return {
    saleId: v.id,
    codigo: v.codigo,
    vendida_em: v.vendida_em,
    veiculo: v.veiculo,
    valor,
    pago,
    abatido,
    // A perdoada não cobra: o saldo dela não entra na conta de ninguém.
    saldo: situacao === 'perdoada' ? 0 : saldo,
    situacao,
    itens: itens.map((i) => ({
      descricao: i.descricao,
      quantidade: Number(i.quantidade),
      subtotal: Number(i.subtotal),
      paga: Boolean(i.id && pagas.has(i.id)),
    })),
  };
}

/** A venda ainda cobra alguma coisa? */
export const cobra = (v: Pick<VendaNoExtrato, 'saldo' | 'situacao'>): boolean =>
  v.situacao !== 'perdoada' && v.saldo > 0.005;

const centavos = (n: number) => Math.round(n * 100) / 100;

/** O extrato: as vendas escolhidas, da mais antiga para a mais nova, e a soma. */
export function montarExtrato(
  cliente: string,
  vendas: VendaNoExtrato[],
  opcoes: { emitidoEm?: string; emitidoPor?: string | null } = {},
): ExtratoDoFiado {
  const ordenadas = [...vendas].sort((a, b) => a.vendida_em.localeCompare(b.vendida_em)
    || a.codigo.localeCompare(b.codigo));
  const somar = (f: (v: VendaNoExtrato) => number) => centavos(ordenadas.reduce((s, v) => s + f(v), 0));
  return {
    cliente,
    emitidoEm: opcoes.emitidoEm ?? new Date().toISOString(),
    emitidoPor: opcoes.emitidoPor ?? null,
    vendas: ordenadas,
    valor: somar((v) => v.valor),
    pago: somar((v) => v.pago),
    abatido: somar((v) => v.abatido),
    saldo: somar((v) => v.saldo),
  };
}

/** A frase curta de cada situação — a mesma no HTML e no PDF. */
export function rotuloDaSituacao(v: VendaNoExtrato): string {
  if (v.situacao === 'perdoada') return 'perdoada';
  if (v.situacao === 'quitada') return 'quitada';
  if (v.situacao === 'parcial') return 'pago em parte';
  return 'em aberto';
}

// --- As linhas, em ordem de leitura -------------------------------------------
//
// O extrato é uma fila de linhas: o cabeçalho de cada venda e, debaixo dele, as
// peças. Paginar é cortar essa fila; quando o corte cai NO MEIO de uma venda,
// a página seguinte repete o cabeçalho dela com "(continuação)" — senão a
// peça solta no topo da folha não diz de qual venda é.

export type LinhaDoExtrato =
  | { tipo: 'venda'; venda: VendaNoExtrato; continuacao: boolean }
  | { tipo: 'item'; item: ItemDoExtrato };

/** Quanto cada tipo de linha ocupa, numa unidade qualquer. */
export interface Geometria {
  venda: number;
  item: number;
  /** Espaço útil de uma página do meio. */
  pagina: number;
  /** Espaço útil da última — menor: ela leva o total e as assinaturas. */
  ultima: number;
}

/**
 * Corta as linhas em páginas.
 *
 * Duas regras além do óbvio: (1) cabeçalho de venda nunca fica sozinho no pé
 * da página — se depois dele não cabe nem uma peça, ele desce junto; (2) a
 * ÚLTIMA página precisa caber o fecho (total e assinaturas). Se o que sobrou
 * não cabe no espaço da última, abre-se mais uma página só para o fecho
 * não ser cortado — é o que a folha do agente faria em silêncio.
 */
export function paginarExtrato(vendas: VendaNoExtrato[], g: Geometria): LinhaDoExtrato[][] {
  const paginas: LinhaDoExtrato[][] = [];
  let atual: LinhaDoExtrato[] = [];
  let usado = 0;

  const fechar = () => { paginas.push(atual); atual = []; usado = 0; };

  for (const venda of vendas) {
    // Cabeçalho + ao menos uma peça (se houver) têm de caber juntos.
    const minimo = g.venda + (venda.itens.length ? g.item : 0);
    if (atual.length && usado + minimo > g.pagina) fechar();
    atual.push({ tipo: 'venda', venda, continuacao: false });
    usado += g.venda;
    for (const item of venda.itens) {
      if (usado + g.item > g.pagina) {
        fechar();
        atual.push({ tipo: 'venda', venda, continuacao: true });
        usado += g.venda;
      }
      atual.push({ tipo: 'item', item });
      usado += g.item;
    }
  }
  // O fecho: a última página tem menos espaço. Se o que está nela passa do
  // espaço da última, ela vira página do meio e o fecho vai sozinho na próxima.
  if (usado > g.ultima) fechar();
  paginas.push(atual);
  return paginas;
}

// --- O papel da loja (HTML, para a fila e para "imprimir aqui") ----------------

/** Medidas do HTML em px CSS, numa folha de 1123 px (ver ESTILO). */
export const GEOMETRIA_HTML: Geometria = { venda: 30, item: 19, pagina: 820, ultima: 560 };

const S_LOGO = `<svg viewBox="0 0 738 502" width="30" style="vertical-align:middle" xmlns="http://www.w3.org/2000/svg">${
  CAMINHOS_MARCA.map((d) => `<path fill="#111111" d="${d}"/>`).join('')}</svg>`;

const ESTILO = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  /* Uma folha A4 por página (794 × 1123 px CSS) — o agente da loja fotografa
     exatamente isso e corta o que passar. A paginação é feita ANTES, em
     paginarExtrato; aqui cada .folha tem altura fixa. */
  body { font-family: Arial, Helvetica, sans-serif; color: #111; font-size: 12px; }
  .folha { width: 794px; height: 1123px; padding: 7mm; overflow: hidden;
           display: flex; flex-direction: column; page-break-after: always; }
  .folha:last-child { page-break-after: auto; }
  header { display: flex; justify-content: space-between; align-items: flex-start;
           border-bottom: 2px solid #111; padding-bottom: 2.5mm; margin-bottom: 3mm; }
  .marca .nome { font-weight: 900; font-style: italic; font-size: 18px; margin-left: 6px; }
  .marca .sub { font-size: 9px; letter-spacing: 2px; margin-left: 6px; color: #555; }
  .marca .loja { display: block; font-size: 9.5px; color: #555; margin-top: 1mm; }
  .doc { text-align: right; }
  .doc .titulo { display: block; font-weight: 900; font-size: 15px; letter-spacing: 1.5px; }
  .doc .quando { display: block; font-size: 10px; color: #555; margin-top: 1mm; }
  .doc .via { display: block; font-size: 10px; font-weight: 700; letter-spacing: 1px;
              text-transform: uppercase; margin-top: 1mm; }
  .quem { border: 2px solid #111; padding: 2.5mm 3mm; margin-bottom: 3mm;
          display: flex; justify-content: space-between; align-items: baseline; }
  .quem .rot { font-size: 9.5px; letter-spacing: 1.5px; text-transform: uppercase; color: #555; }
  .quem .nome { font-size: 16px; font-weight: 900; }
  .quem .deve { text-align: right; }
  .quem .deve .valor { font-size: 18px; font-weight: 900; }
  table { width: 100%; border-collapse: collapse; }
  th { font-size: 9px; text-transform: uppercase; letter-spacing: 1px; color: #555;
       text-align: left; border-bottom: 1px solid #111; padding-bottom: 1mm; }
  tr.venda td { height: 30px; padding-top: 2mm; font-weight: 700; font-size: 12px;
                border-bottom: 1px solid #111; vertical-align: bottom; white-space: nowrap; }
  tr.venda td.carro { font-weight: 400; color: #444; white-space: normal; overflow: hidden; }
  tr.item td { height: 19px; font-size: 11px; color: #222; border-bottom: 1px solid #ececec;
               white-space: nowrap; overflow: hidden; }
  tr.item td.desc { padding-left: 4mm; max-width: 0; text-overflow: ellipsis; }
  .num { text-align: right; white-space: nowrap; }
  .cont { font-weight: 400; color: #666; font-size: 10px; }
  .situ { font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; }
  .miolo { flex: 1; }
  .segue { text-align: right; font-size: 10px; color: #555; margin-top: 2mm; }
  .fecho { margin-top: 3mm; }
  .contas { margin-left: auto; width: 62%; font-size: 12px; }
  .contas div { display: flex; justify-content: space-between; padding: .8mm 0; }
  .total { display: flex; justify-content: space-between; align-items: baseline;
           border: 3px solid #111; padding: 2.5mm 3mm; margin-top: 1.5mm; }
  .total .rot { font-size: 11px; font-weight: 900; letter-spacing: 1.5px; text-transform: uppercase; }
  .total .valor { font-size: 24px; font-weight: 900; }
  .declara { font-size: 11px; line-height: 1.45; margin-top: 4mm; }
  .assinaturas { display: flex; gap: 10mm; margin-top: 14mm; }
  .assinaturas span { flex: 1; border-top: 1px solid #111; padding-top: 1.5mm;
                      font-size: 10px; text-align: center; }
  .termos { font-size: 9px; line-height: 1.4; color: #444; margin-top: 4mm;
            border-top: 1px solid #ddd; padding-top: 2mm; }
  .rodape { font-size: 9px; color: #777; display: flex; justify-content: space-between;
            margin-top: 2mm; }
  @media print { @page { size: A4; margin: 0; } }
`;

export interface OpcoesDoPapel {
  /** 'cliente' | 'loja' carimba a via; null não carimba (cópia avulsa). */
  via?: 'cliente' | 'loja' | null;
  /** As linhas de assinatura no fim (padrão: sim). */
  assinatura?: boolean;
}

const nomeDaVia = (via: OpcoesDoPapel['via']) =>
  via === 'cliente' ? 'Via do cliente' : via === 'loja' ? 'Via da loja' : '';

function cabecalhoHtml(e: ExtratoDoFiado, via: OpcoesDoPapel['via']): string {
  const fones = [telefoneLegivel(negocio.telefoneFixo), telefoneLegivel(negocio.whatsapp)]
    .filter(Boolean).join(' · ');
  return `<header>
    <div class="marca">
      ${S_LOGO}<span class="nome">SERGINHO</span><span class="sub">AUTO PEÇAS</span>
      <span class="loja">
        ${escaparHtml(enderecoEmLinha())}${fones ? ` · ${escaparHtml(fones)}` : ''}
        ${negocio.cnpj ? `<br>CNPJ ${escaparHtml(negocio.cnpj)}` : ''}
      </span>
    </div>
    <div class="doc">
      <span class="titulo">EXTRATO DE FIADO</span>
      <span class="quando">posição em ${escaparHtml(dataHoraBR(e.emitidoEm))}</span>
      ${via ? `<span class="via">${escaparHtml(nomeDaVia(via))}</span>` : ''}
    </div>
  </header>`;
}

function linhaHtml(l: LinhaDoExtrato): string {
  if (l.tipo === 'item') {
    return `<tr class="item">
      <td class="desc">${escaparHtml(qtd(l.item.quantidade))} × ${escaparHtml(l.item.descricao)}${
        l.item.paga ? ' <span class="cont">(paga)</span>' : ''}</td>
      <td class="num">${escaparHtml(brlTela(l.item.subtotal))}</td>
      <td></td><td></td>
    </tr>`;
  }
  const v = l.venda;
  return `<tr class="venda">
    <td>${escaparHtml(dataBR(v.vendida_em))} · ${escaparHtml(v.codigo)}${
      l.continuacao ? ' <span class="cont">(continuação)</span>' : ''}${
      v.veiculo && !l.continuacao ? ` <span class="carro" style="font-weight:400;color:#444">· ${escaparHtml(v.veiculo)}</span>` : ''}</td>
    <td class="num">${l.continuacao ? '' : escaparHtml(brlTela(v.valor))}</td>
    <td class="num">${l.continuacao || v.pago + v.abatido <= 0.005 ? '' : `− ${escaparHtml(brlTela(v.pago + v.abatido))}`}</td>
    <td class="num">${l.continuacao ? '' : v.situacao === 'perdoada' || v.situacao === 'quitada'
      ? `<span class="situ">${escaparHtml(rotuloDaSituacao(v))}</span>`
      : escaparHtml(brlTela(v.saldo))}</td>
  </tr>`;
}

function fechoHtml(e: ExtratoDoFiado, assinatura: boolean): string {
  return `<div class="fecho">
    <div class="contas">
      <div><span>Compras</span><span>${escaparHtml(brlTela(e.valor))}</span></div>
      ${e.pago > 0.005 ? `<div><span>Já pago</span><span>− ${escaparHtml(brlTela(e.pago))}</span></div>` : ''}
      ${e.abatido > 0.005 ? `<div><span>Descontos e devoluções</span><span>− ${escaparHtml(brlTela(e.abatido))}</span></div>` : ''}
      <div class="total"><span class="rot">Total em aberto</span>
        <span class="valor">${escaparHtml(brlTela(e.saldo))}</span></div>
    </div>
    ${assinatura ? `<p class="declara">
      Confiro as compras acima e reconheço dever à ${escaparHtml(negocio.nome)} o valor de
      <strong>${escaparHtml(brlTela(e.saldo))}</strong>, referente a
      ${e.vendas.filter(cobra).length === 1 ? '1 compra' : `${e.vendas.filter(cobra).length} compras`} a prazo (fiado).
    </p>
    <div class="assinaturas">
      <span>${escaparHtml(e.cliente)} — assinatura e data</span>
      <span>${escaparHtml(negocio.nome)}${e.emitidoPor ? ` — ${escaparHtml(e.emitidoPor)}` : ''}</span>
    </div>` : ''}
    <p class="termos"><strong>Este documento NÃO é documento fiscal.</strong> É o extrato das
      compras a prazo para conferência do cliente. Os valores são os do sistema na data acima;
      pagamentos feitos depois não aparecem nele.</p>
  </div>`;
}

/**
 * As páginas do extrato, cada uma um documento HTML completo — UMA por
 * trabalho da fila, porque o agente imprime uma folha por trabalho.
 */
export function paginasDoExtratoHtml(e: ExtratoDoFiado, opcoes: OpcoesDoPapel = {}): string[] {
  const assinatura = opcoes.assinatura ?? true;
  const via = opcoes.via ?? null;
  const paginas = paginarExtrato(e.vendas, GEOMETRIA_HTML);
  return paginas.map((linhas, i) => documento(e, `${folhaHtml(e, linhas, i, paginas.length, via, assinatura)}`));
}

function folhaHtml(
  e: ExtratoDoFiado, linhas: LinhaDoExtrato[], i: number, total: number,
  via: OpcoesDoPapel['via'], assinatura: boolean,
): string {
  const ultima = i === total - 1;
  return `<div class="folha">
    ${cabecalhoHtml(e, via)}
    <div class="quem">
      <div><div class="rot">Cliente</div><div class="nome">${escaparHtml(e.cliente)}</div></div>
      <div class="deve"><div class="rot">Total em aberto</div>
        <div class="valor">${escaparHtml(brlTela(e.saldo))}</div></div>
    </div>
    <div class="miolo">
      ${linhas.length ? `<table>
        <colgroup><col><col style="width:22%"><col style="width:17%"><col style="width:17%"></colgroup>
        <tr><th>Compra</th><th class="num">Valor</th><th class="num">Pago/abatido</th><th class="num">Em aberto</th></tr>
        ${linhas.map(linhaHtml).join('')}
      </table>` : ''}
      ${ultima ? '' : '<p class="segue">continua na próxima folha →</p>'}
    </div>
    ${ultima ? fechoHtml(e, assinatura) : ''}
    <div class="rodape"><span>${escaparHtml(nomeDaVia(via))}</span>
      <span>Folha ${i + 1} de ${total} · ${escaparHtml(e.cliente)}</span></div>
  </div>`;
}

function documento(e: ExtratoDoFiado, corpo: string): string {
  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy"
      content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; script-src 'none'; form-action 'none'">
<title>Extrato de fiado — ${escaparHtml(e.cliente)}</title>
<style>${ESTILO}</style></head>
<body>${corpo}</body></html>`;
}

/**
 * O documento inteiro numa janela só — "imprimir aqui", no computador ou no
 * celular. As folhas já vêm com quebra de página entre elas, e é daqui que se
 * escolhe "Salvar como PDF" no diálogo de impressão do computador.
 */
export function extratoParaImprimirAqui(e: ExtratoDoFiado, opcoes: OpcoesDoPapel & { vias?: 1 | 2 } = {}): string {
  const assinatura = opcoes.assinatura ?? true;
  const paginas = paginarExtrato(e.vendas, GEOMETRIA_HTML);
  const vias: Array<OpcoesDoPapel['via']> = opcoes.vias === 2 ? ['cliente', 'loja'] : [opcoes.via ?? null];
  const corpo = vias.map((via) => paginas
    .map((linhas, i) => folhaHtml(e, linhas, i, paginas.length, via, assinatura)).join('')).join('');
  return documento(e, corpo);
}

// --- O arquivo (PDF, para mandar à oficina) -----------------------------------

const MARGEM = 16;
const LARGURA = 210;
const DIREITA = LARGURA - MARGEM;
const COL_VALOR = 132;
const COL_PAGO = 162;
const COL_ABERTO = DIREITA;

/** Medidas do PDF em mm (o miolo vai de ~62 a ~280 mm). */
export const GEOMETRIA_PDF: Geometria = { venda: 8, item: 5, pagina: 212, ultima: 150 };

// R$ com espaço COMUM: o Intl põe um espaço inquebrável que a tabela de
// larguras não conhece, e o alinhamento à direita escorregaria.
const brl = (n: number) => `R$ ${n.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;

/** Corta o texto com reticências até caber na largura. */
function caber(texto: string, larguraMm: number, tamanho: number, negrito = false): string {
  if (larguraDoTexto(texto, tamanho, negrito) <= larguraMm) return texto;
  let t = texto;
  while (t.length > 1 && larguraDoTexto(`${t}…`, tamanho, negrito) > larguraMm) t = t.slice(0, -1);
  return `${t.trimEnd()}…`;
}

function cabecalhoPdf(pdf: Pdf, e: ExtratoDoFiado, folha: number, total: number): number {
  const LADO = 14;
  for (const d of CAMINHOS_MARCA) {
    pdf.caminho(interpretarCaminho(d), VIEWBOX_MARCA as unknown as readonly [number, number, number, number],
      { xMm: MARGEM, yMm: MARGEM, larguraMm: LADO }, 0.1);
  }
  const x = MARGEM + LADO + 5;
  pdf.texto(negocio.nome, x, MARGEM + 4.5, { tamanho: 14, negrito: true });
  let y = MARGEM + 9;
  const linha = (t: string | null) => {
    if (!t) return;
    pdf.texto(t, x, y, { tamanho: 7.5, cinza: 0.35 });
    y += 3.4;
  };
  if (negocio.cnpj) linha(`CNPJ ${negocio.cnpj}`);
  linha(enderecoDaLoja());
  linha([telefoneLegivel(negocio.telefoneFixo), telefoneLegivel(negocio.whatsapp)].filter(Boolean).join(' · ') || null);

  pdf.texto('EXTRATO DE FIADO', DIREITA, MARGEM + 4.5, { tamanho: 12, negrito: true, alinhamento: 'direita' });
  pdf.texto(`posição em ${dataHoraBR(e.emitidoEm)}`, DIREITA, MARGEM + 9, { tamanho: 8, cinza: 0.35, alinhamento: 'direita' });
  if (total > 1) {
    pdf.texto(`folha ${folha} de ${total}`, DIREITA, MARGEM + 12.4, { tamanho: 8, cinza: 0.35, alinhamento: 'direita' });
  }

  const base = Math.max(y, MARGEM + LADO) + 3;
  pdf.linha(MARGEM, base, DIREITA, base, { espessura: 0.6 });

  // A pessoa e o que ela deve — em toda folha, para a folha solta se explicar.
  const yq = base + 4;
  pdf.retangulo(MARGEM, yq, DIREITA - MARGEM, 14, 0.94);
  pdf.texto('CLIENTE', MARGEM + 3, yq + 4.5, { tamanho: 7, cinza: 0.4 });
  pdf.texto(caber(e.cliente, 110, 13, true), MARGEM + 3, yq + 10.5, { tamanho: 13, negrito: true });
  pdf.texto('TOTAL EM ABERTO', DIREITA - 3, yq + 4.5, { tamanho: 7, cinza: 0.4, alinhamento: 'direita' });
  pdf.texto(brl(e.saldo), DIREITA - 3, yq + 10.5, { tamanho: 13, negrito: true, alinhamento: 'direita' });

  const yt = yq + 21;
  pdf.texto('COMPRA', MARGEM, yt, { tamanho: 7, negrito: true, cinza: 0.35 });
  pdf.texto('VALOR', COL_VALOR, yt, { tamanho: 7, negrito: true, cinza: 0.35, alinhamento: 'direita' });
  pdf.texto('PAGO/ABATIDO', COL_PAGO, yt, { tamanho: 7, negrito: true, cinza: 0.35, alinhamento: 'direita' });
  pdf.texto('EM ABERTO', COL_ABERTO, yt, { tamanho: 7, negrito: true, cinza: 0.35, alinhamento: 'direita' });
  pdf.linha(MARGEM, yt + 1.5, DIREITA, yt + 1.5, { espessura: 0.3 });
  return yt + 1.5;
}

/** O extrato em PDF — o arquivo que vai para o WhatsApp da oficina. */
export function montarPdfFiado(e: ExtratoDoFiado, opcoes: { assinatura?: boolean } = {}): Uint8Array {
  const assinatura = opcoes.assinatura ?? true;
  const paginas = paginarExtrato(e.vendas, GEOMETRIA_PDF);
  const pdf = novoPdf();

  paginas.forEach((linhas, i) => {
    if (i > 0) pdf.novaPagina();
    let y = cabecalhoPdf(pdf, e, i + 1, paginas.length);

    for (const l of linhas) {
      if (l.tipo === 'venda') {
        const v = l.venda;
        y += GEOMETRIA_PDF.venda;
        const titulo = `${dataBR(v.vendida_em)} · ${v.codigo}`;
        pdf.texto(titulo, MARGEM, y - 1.5, { tamanho: 9, negrito: true });
        const xCarro = MARGEM + larguraDoTexto(titulo, 9, true) + 2;
        const resto = l.continuacao ? '(continuação)' : v.veiculo ? `· ${v.veiculo}` : '';
        if (resto) pdf.texto(caber(resto, COL_VALOR - 24 - xCarro, 8.5), xCarro, y - 1.5, { tamanho: 8.5, cinza: 0.35 });
        if (!l.continuacao) {
          pdf.texto(brl(v.valor), COL_VALOR, y - 1.5, { tamanho: 9, negrito: true, alinhamento: 'direita' });
          if (v.pago + v.abatido > 0.005) {
            pdf.texto(`- ${brl(v.pago + v.abatido)}`, COL_PAGO, y - 1.5, { tamanho: 9, alinhamento: 'direita' });
          }
          const fim = v.situacao === 'quitada' || v.situacao === 'perdoada'
            ? rotuloDaSituacao(v).toUpperCase() : brl(v.saldo);
          pdf.texto(fim, COL_ABERTO, y - 1.5, { tamanho: 9, negrito: true, alinhamento: 'direita' });
        }
        pdf.linha(MARGEM, y, DIREITA, y, { espessura: 0.3 });
      } else {
        y += GEOMETRIA_PDF.item;
        const texto = `${qtd(l.item.quantidade)} × ${l.item.descricao}${l.item.paga ? ' (paga)' : ''}`;
        pdf.texto(caber(texto, COL_VALOR - 26 - MARGEM - 4, 8.5), MARGEM + 4, y - 1.4, { tamanho: 8.5, cinza: 0.15 });
        pdf.texto(brl(l.item.subtotal), COL_VALOR, y - 1.4, { tamanho: 8.5, cinza: 0.15, alinhamento: 'direita' });
        pdf.linha(MARGEM + 4, y, DIREITA, y, { espessura: 0.1, cinza: 0.85 });
      }
    }

    if (i < paginas.length - 1) {
      pdf.texto('continua na próxima folha', DIREITA, y + 6, { tamanho: 8, cinza: 0.4, alinhamento: 'direita' });
      return;
    }

    // O fecho.
    y += 9;
    const xRot = 100;
    const conta = (rot: string, valor: string) => {
      pdf.texto(rot, xRot, y, { tamanho: 9.5 });
      pdf.texto(valor, DIREITA, y, { tamanho: 9.5, alinhamento: 'direita' });
      y += 5;
    };
    conta('Compras', brl(e.valor));
    if (e.pago > 0.005) conta('Já pago', `- ${brl(e.pago)}`);
    if (e.abatido > 0.005) conta('Descontos e devoluções', `- ${brl(e.abatido)}`);
    pdf.retangulo(xRot - 3, y - 2, DIREITA - xRot + 3, 11, 0.1);
    pdf.texto('TOTAL EM ABERTO', xRot, y + 5.2, { tamanho: 9, negrito: true, cinza: 1 });
    pdf.texto(brl(e.saldo), DIREITA - 2, y + 5.6, { tamanho: 14, negrito: true, cinza: 1, alinhamento: 'direita' });
    y += 17;

    if (assinatura) {
      const n = e.vendas.filter(cobra).length;
      const frase = `Confiro as compras acima e reconheço dever à ${negocio.nome} o valor de ${brl(e.saldo)}, `
        + `referente a ${n === 1 ? '1 compra' : `${n} compras`} a prazo (fiado).`;
      for (const l of quebrarEmLinhas(frase, DIREITA - MARGEM, 9.5)) {
        pdf.texto(l, MARGEM, y, { tamanho: 9.5 });
        y += 4.6;
      }
      y += 16;
      const meio = (MARGEM + DIREITA) / 2;
      pdf.linha(MARGEM, y, meio - 6, y, { espessura: 0.3 });
      pdf.linha(meio + 6, y, DIREITA, y, { espessura: 0.3 });
      pdf.texto(caber(`${e.cliente} — assinatura e data`, meio - 6 - MARGEM, 8), (MARGEM + meio - 6) / 2, y + 4,
        { tamanho: 8, alinhamento: 'centro' });
      pdf.texto(caber(`${negocio.nome}${e.emitidoPor ? ` — ${e.emitidoPor}` : ''}`, DIREITA - meio - 6, 8),
        (meio + 6 + DIREITA) / 2, y + 4, { tamanho: 8, alinhamento: 'centro' });
      y += 10;
    }

    const termos = 'Este documento NÃO é documento fiscal. É o extrato das compras a prazo para conferência '
      + 'do cliente. Os valores são os do sistema na data acima; pagamentos feitos depois não aparecem nele.';
    for (const l of quebrarEmLinhas(termos, DIREITA - MARGEM, 7.5)) {
      pdf.texto(l, MARGEM, y, { tamanho: 7.5, cinza: 0.4 });
      y += 3.5;
    }
  });

  return pdf.bytes();
}

/** "fiado-oficina-do-ze-2026-10-14.pdf" — sem acento nem espaço. */
export function nomeDoArquivoFiado(e: ExtratoDoFiado): string {
  const nome = e.cliente.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'cliente';
  return `fiado-${nome}-${hojeISO(new Date(e.emitidoEm))}.pdf`;
}
